import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronDown,
    ChevronsUpDown,
    ChevronUp,
    Columns,
    Download,
    Filter,
    SortAsc,
    SortDesc,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Checkbox } from './checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Skeleton } from './skeleton';

export interface Column<T = any> {
    id: string;
    label: string;
    key: keyof T;
    sortable?: boolean;
    width?: string;
    render?: (value: any, item: T) => React.ReactNode;
}

export interface SortConfig<T = any> {
    key: keyof T;
    direction: 'asc' | 'desc';
}

export interface FilterConfig {
    [key: string]: any;
}

export interface FilterOption {
    key: string;
    label: string;
    type: 'select' | 'input' | 'date' | 'number';
    options?: { value: string; label: string }[];
    placeholder?: string;
}

export interface ActionConfig<T = any> {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    variant?:
        | 'default'
        | 'destructive'
        | 'success'
        | 'warning'
        | 'outline'
        | 'secondary'
        | 'ghost'
        | 'link';
    size?: 'default' | 'sm' | 'lg' | 'icon' | 'icon-sm';
    onClick: (item: T) => void;
    disabled?: (item: T) => boolean;
    hidden?: (item: T) => boolean;
}

export interface DataTableProps<T = any> {
    data: T[];
    columns: Column<T>[];
    isLoading: boolean;
    onExport?: () => void;
    sortConfig: SortConfig<T>;
    onSort: (key: keyof T) => void;
    filters: FilterConfig;
    onFilterChange: (filters: FilterConfig) => void;
    selectedItems: string[];
    onSelectionChange: (itemIds: string[]) => void;
    filterOptions?: FilterOption[];
    actions?: ActionConfig<T>[];
    getItemId: (item: T) => string;
    getItemName?: (item: T) => string;
    onRowClick?: (item: T) => void;
    emptyMessage?: string;
    loadingMessage?: string;
    skeletonRows?: number;
    showAdvancedFilters?: boolean;
    showColumnSettings?: boolean;
    showSelection?: boolean;
    showActions?: boolean;
    className?: string;
    /**
     * Fill the parent's height, scroll the rows only, and pin the column header.
     * The page must render inside `<PageContainer fullHeight>` for this to work.
     */
    stickyLayout?: boolean;
    /** Row height. CRM lists default to `compact`. */
    density?: 'comfortable' | 'compact';
    /** Replaces the plain "no data" line. Use `<EmptyState>`. */
    emptyState?: React.ReactNode;
    /**
     * Sorting is performed by the backend, so rows are rendered in the order
     * received. Leave false only for tables that are not paginated server-side.
     */
    serverSort?: boolean;
}

const DataTable = <T extends Record<string, any>>({
    data,
    columns,
    isLoading,
    onExport,
    sortConfig,
    onSort,
    filters,
    onFilterChange,
    selectedItems,
    onSelectionChange,
    filterOptions = [],
    actions = [],
    getItemId,
    getItemName,
    onRowClick,
    emptyMessage = 'No data found',
    loadingMessage = 'Loading...',
    skeletonRows = 10,
    showAdvancedFilters = true,
    showColumnSettings = true,
    showSelection = true,
    showActions = true,
    className = '',
    stickyLayout = false,
    density = 'compact',
    emptyState,
    serverSort = true,
}: DataTableProps<T>) => {
    const navigate = useNavigate();
    // Compact is the CRM default: a marketing-card row height wastes a screen of rows.
    const densityCell = density === 'compact' ? 'py-2 px-3 text-sm' : 'py-4 px-4';
    const densityHead = density === 'compact' ? 'h-9 px-3' : 'h-12 px-4';
    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
    const [showColumnSettingsDialog, setShowColumnSettingsDialog] = useState(false);
    const [showAdvancedFiltersPanel, setShowAdvancedFiltersPanel] = useState(false);

    // Initialize visible columns if not set
    React.useEffect(() => {
        if (visibleColumns.length === 0) {
            setVisibleColumns(columns.map(col => col.id));
        }
    }, [visibleColumns.length, columns]);

    const availableColumns = useMemo(() => {
        return columns.filter(col => col.id !== 'selection' && col.id !== 'actions');
    }, [columns]);

    const handleColumnToggle = (columnId: string) => {
        setVisibleColumns(prev =>
            prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            onSelectionChange(data.map(item => getItemId(item)));
        } else {
            onSelectionChange([]);
        }
    };

    const handleSelectItem = (itemId: string, checked: boolean) => {
        if (checked) {
            onSelectionChange([...selectedItems, itemId]);
        } else {
            onSelectionChange(selectedItems.filter(id => id !== itemId));
        }
    };

    const filteredAndSortedData = useMemo(() => {
        // Filtering is always handled by the backend.
        // Sorting too, when `serverSort` is set: sorting only the current page
        // client-side produces an order that is wrong across page boundaries.
        if (serverSort || !sortConfig.key) {
            return data;
        }

        // Copy before sorting — Array.prototype.sort mutates, and `data` is a prop.
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key as keyof T];
            const bValue = b[sortConfig.key as keyof T];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            const comparison = String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [data, sortConfig, serverSort]);

    const visibleColumnsConfig = columns.filter(col => visibleColumns.includes(col.id));

    // Add selection column if enabled
    const finalColumns = useMemo(() => {
        const cols = [...visibleColumnsConfig];

        if (showSelection) {
            const selectionColumn: Column<T> = {
                id: 'selection',
                label: '',
                key: 'id' as keyof T,
                width: '50px',
                render: (_, item) => (
                    <Checkbox
                        checked={selectedItems.includes(getItemId(item))}
                        onCheckedChange={checked =>
                            handleSelectItem(getItemId(item), checked as boolean)
                        }
                    />
                ),
            };
            cols.unshift(selectionColumn);
        }

        if (showActions && actions.length > 0) {
            const actionsColumn: Column<T> = {
                id: 'actions',
                label: 'Actions',
                key: 'id' as keyof T,
                width: '120px',
                render: (_, item) => (
                    <div className="flex justify-end gap-1">
                        {actions.map(action => {
                            if (action.hidden && action.hidden(item)) return null;

                            const itemName = getItemName?.(item);
                            const label = itemName ? `${action.label}: ${itemName}` : action.label;

                            return (
                                <Button
                                    key={action.id}
                                    variant={action.variant || 'ghost'}
                                    size={action.size || 'icon-sm'}
                                    onClick={e => {
                                        e.stopPropagation();
                                        action.onClick(item);
                                    }}
                                    disabled={action.disabled ? action.disabled(item) : false}
                                    title={action.label}
                                    aria-label={label}
                                >
                                    <action.icon className="h-4 w-4" />
                                </Button>
                            );
                        })}
                    </div>
                ),
            };
            cols.push(actionsColumn);
        }

        return cols;
    }, [visibleColumnsConfig, showSelection, showActions, actions, selectedItems, getItemId]);

    return (
        <div className={cn('flex flex-col gap-4', stickyLayout && 'min-h-0 flex-1', className)}>
            {/* Table Controls */}
            <div className="flex shrink-0 items-center justify-between">
                <div className="flex items-center gap-2">
                    {showColumnSettings && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowColumnSettingsDialog(true)}
                            className="flex items-center gap-2"
                        >
                            <Columns className="h-4 w-4" />
                            Columns
                        </Button>
                    )}
                    {showAdvancedFilters && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAdvancedFiltersPanel(!showAdvancedFiltersPanel)}
                            className="flex items-center gap-2"
                        >
                            <Filter className="h-4 w-4" />
                            Filters
                            {showAdvancedFiltersPanel ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </Button>
                    )}
                    {showSelection && selectedItems.length > 0 && (
                        <Badge variant="secondary">{selectedItems.length} selected</Badge>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/* Export button moved to table settings menu */}
                </div>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && showAdvancedFiltersPanel && filterOptions.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Advanced Filters</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-4 gap-4">
                            {filterOptions.map(option => (
                                <div key={option.key} className="space-y-2">
                                    <label className="text-sm font-medium">{option.label}</label>
                                    {option.type === 'select' && option.options && (
                                        <Select
                                            value={filters[option.key] || ''}
                                            onValueChange={value =>
                                                onFilterChange({ ...filters, [option.key]: value })
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue
                                                    placeholder={
                                                        option.placeholder ||
                                                        `All ${option.label.toLowerCase()}`
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">
                                                    All {option.label.toLowerCase()}
                                                </SelectItem>
                                                {option.options.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                    {option.type === 'input' && (
                                        <Input
                                            placeholder={
                                                option.placeholder ||
                                                `Filter by ${option.label.toLowerCase()}`
                                            }
                                            value={filters[option.key] || ''}
                                            onChange={e =>
                                                onFilterChange({
                                                    ...filters,
                                                    [option.key]: e.target.value,
                                                })
                                            }
                                        />
                                    )}
                                    {option.type === 'date' && (
                                        <Input
                                            type="date"
                                            value={filters[option.key] || ''}
                                            onChange={e =>
                                                onFilterChange({
                                                    ...filters,
                                                    [option.key]: e.target.value,
                                                })
                                            }
                                        />
                                    )}
                                    {option.type === 'number' && (
                                        <Input
                                            type="number"
                                            placeholder={
                                                option.placeholder ||
                                                `Minimum ${option.label.toLowerCase()}`
                                            }
                                            value={filters[option.key] || ''}
                                            onChange={e =>
                                                onFilterChange({
                                                    ...filters,
                                                    [option.key]: e.target.value,
                                                })
                                            }
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card className={cn(stickyLayout && 'flex flex-1 flex-col overflow-hidden')}>
                <div
                    className={cn(
                        'overflow-auto',
                        // The rows own the vertical scrolling: that is what lets the
                        // column header stay pinned, since `position: sticky` resolves
                        // against the nearest scrolling ancestor. The max-height is what
                        // actually makes this box scroll — `flex-1` alone does nothing
                        // once the page is min-height rather than fixed-height. The
                        // min-height stops the table being squeezed to a few pixels
                        // under a tall stack of stat tiles and filters.
                        stickyLayout && 'min-h-[22rem] max-h-[68vh] flex-1',
                        !stickyLayout && 'max-h-[70vh]'
                    )}
                >
                    <Table className="w-max min-w-full" unwrapped={stickyLayout}>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                {finalColumns.map(column => {
                                    const isActionsColumn = column.id === 'actions';
                                    const isSorted = sortConfig.key === column.key;

                                    return (
                                        <TableHead
                                            key={column.id}
                                            style={{ width: column.width }}
                                            aria-sort={
                                                column.sortable
                                                    ? isSorted
                                                        ? sortConfig.direction === 'asc'
                                                            ? 'ascending'
                                                            : 'descending'
                                                        : 'none'
                                                    : undefined
                                            }
                                            className={cn(
                                                // Sticky lives on the cells, not on
                                                // <thead>: sticking a table section is
                                                // less reliable across browsers.
                                                'sticky top-0 z-20 bg-card',
                                                densityHead,
                                                isActionsColumn &&
                                                    'z-30 border-s border-border sticky right-0'
                                            )}
                                        >
                                            {column.id === 'selection' ? (
                                                <Checkbox
                                                    aria-label="Select all rows on this page"
                                                    checked={
                                                        selectedItems.length === data.length &&
                                                        data.length > 0
                                                    }
                                                    onCheckedChange={handleSelectAll}
                                                />
                                            ) : column.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onSort(column.key)}
                                                    className="-mx-1 inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                    title={`Sort by ${column.label}`}
                                                >
                                                    {column.label}
                                                    {!isSorted && (
                                                        <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                                                    )}
                                                    {isSorted &&
                                                        (sortConfig.direction === 'asc' ? (
                                                            <SortAsc className="h-3.5 w-3.5" />
                                                        ) : (
                                                            <SortDesc className="h-3.5 w-3.5" />
                                                        ))}
                                                </button>
                                            ) : (
                                                column.label
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                                    <TableRow
                                        key={`skeleton-${rowIndex}`}
                                        className="hover:bg-transparent"
                                    >
                                        {finalColumns.map(column => (
                                            <TableCell
                                                key={column.id}
                                                className={cn(
                                                    densityCell,
                                                    column.id === 'actions' &&
                                                        'sticky right-0 bg-card border-s border-border'
                                                )}
                                            >
                                                {column.id === 'selection' ? (
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                ) : (
                                                    <Skeleton
                                                        className="h-4"
                                                        style={{
                                                            width: `${60 + ((rowIndex * 7 + finalColumns.indexOf(column) * 13) % 35)}%`,
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : filteredAndSortedData.length === 0 ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell colSpan={finalColumns.length} className="p-0">
                                        {emptyState ?? (
                                            <EmptyState title={emptyMessage} size="sm" />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredAndSortedData.map(item => (
                                    <TableRow
                                        key={getItemId(item)}
                                        className={cn(
                                            'group transition-colors',
                                            onRowClick && 'cursor-pointer hover:bg-muted/50'
                                        )}
                                        onClick={e => {
                                            // Don't navigate if clicking on checkbox, actions, or their containers
                                            if (
                                                onRowClick &&
                                                !(e.target as HTMLElement).closest(
                                                    '[data-selection-cell], [data-actions-cell]'
                                                )
                                            ) {
                                                onRowClick(item);
                                            }
                                        }}
                                    >
                                        {finalColumns.map(column => (
                                            <TableCell
                                                key={column.id}
                                                data-selection-cell={
                                                    column.id === 'selection' ? 'true' : undefined
                                                }
                                                data-actions-cell={
                                                    column.id === 'actions' ? 'true' : undefined
                                                }
                                                className={cn(
                                                    densityCell,
                                                    column.id === 'actions' &&
                                                        'sticky right-0 border-s border-border bg-card group-hover:bg-muted/50'
                                                )}
                                            >
                                                {column.render
                                                    ? column.render(item[column.key], item)
                                                    : String(item[column.key] || '')}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            {/* Column Settings Dialog */}
            {showColumnSettings && (
                <Dialog open={showColumnSettingsDialog} onOpenChange={setShowColumnSettingsDialog}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>Customize Columns</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Select columns to display:
                                </label>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {availableColumns.map(column => (
                                        <div
                                            key={column.id}
                                            className="flex items-center space-x-2"
                                        >
                                            <Checkbox
                                                id={`data-table-col-${column.id}`}
                                                checked={visibleColumns.includes(column.id)}
                                                onCheckedChange={checked =>
                                                    handleColumnToggle(column.id)
                                                }
                                            />
                                            <label
                                                htmlFor={`data-table-col-${column.id}`}
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                            >
                                                {column.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setVisibleColumns(columns.map(col => col.id))}
                            >
                                Reset to Default
                            </Button>
                            <Button onClick={() => setShowColumnSettingsDialog(false)}>
                                Apply
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default DataTable;
